import { Ref } from '@solid-primitives/refs';

import { Style, animate } from './animate';

export function Flip(props: FlipProps) {
  const { duration, zoom = true, align = ['left', 'top'], easing } = props;

  return (
    <Ref
      ref={(el) => {
        if (!el) return;
        let counterpart: ReturnType<typeof animate> | undefined;
        let from: DOMRect | undefined;
        createComputed(() => {
          props.track;

          if (from) {
            createEffect(() => {
              counterpart = animate(
                el,
                {
                  duration,
                  easing,
                  css: flip(el, from!, el.getBoundingClientRect(), {
                    zoom,
                    align,
                  }),
                },
                undefined,
                1
              );
            });
          }
          onCleanup(() => {
            from = el.getBoundingClientRect();
            counterpart?.abort();
          });
        });
      }}
    >
      {props.children}
    </Ref>
  );
}

function flip(
  node: Element,
  from: DOMRect,
  to: DOMRect,
  options: {
    zoom: boolean;
    align: Align;
  }
): (t: number) => Style {
  var style = getComputedStyle(node);
  var transform = style.transform === 'none' ? '' : style.transform;
  var [ox, oy] = style.transformOrigin.split(' ').map(parseFloat);
  ox /= node.clientWidth;
  oy /= node.clientHeight;
  if (options.zoom) {
    const zoom = get_zoom(node);
    const [alignX, alignY] = options.align;
    const sx = node.clientWidth / to.width / zoom;
    const sy = node.clientHeight / to.height / zoom;
    const fx = from[alignX] + from.width * ox;
    const fy = from[alignY] + from.height * oy;
    const tx = to[alignX] + to.width * ox;
    const ty = to[alignY] + to.height * oy;
    const dx = (fx - tx) * sx;
    const dy = (fy - ty) * sy;
    const dsx = from.width / to.width;
    const dsy = from.height / to.height;

    return (t) => {
      const u = 1 - t;
      const x = u * dx;
      const y = u * dy;
      const sx2 = t + u * dsx;
      const sy2 = t + u * dsy;
      return {
        transform: `${transform} translate(${x}px, ${y}px) scale(${sx2}, ${sy2})`,
      };
    };
  } else {
    const [alignX, alignY] = options.align;
    const isLeft = alignX === 'left';
    const isTop = alignY === 'top';
    const fx = from[alignX] + (isLeft ? from.width * ox : 0);
    const fy = from[alignY] + (isTop ? from.height * oy : 0);
    const tx = to[alignX] + (isLeft ? to.width * ox : 0);
    const ty = to[alignY] + (isTop ? to.height * oy : 0);
    const dx = fx - tx;
    const dy = fy - ty;

    return (t) => {
      const u = 1 - t;
      const x = u * dx;
      const y = u * dy;
      return {
        transform: `${transform} translate(${x}px, ${y}px)`,
      };
    };
  }
}

function get_zoom(element: Element) {
  if ('currentCSSZoom' in element) {
    return element.currentCSSZoom;
  }
  let current: Element | null = element;
  let zoom = 1;
  while (current !== null) {
    zoom *= +getComputedStyle(current).zoom;
    current = (current as Element).parentElement;
  }
  return zoom;
}

type Align = ['left' | 'right', 'top' | 'bottom'];

interface FlipProps extends ParentProps {
  duration?: number;
  easing?(t: number): number;
  zoom?: boolean;
  align?: Align;
  track?: any;
}
